import { Button, Segmented, Table, Tag, Tooltip, Typography, type TableColumnsType } from "antd";
import type { SortOrder } from "antd/es/table/interface";
import { useMemo, useState, type Key, type ReactNode } from "react";
import { TbLock, TbPencil } from "react-icons/tb";
import type {
  T_AssistantContextData,
  T_AssistantOffer,
  T_AssistantTurn,
  T_AssistantTurnMeta,
  T_GradeKey,
  T_LimitKey,
  T_RejectedVariantsData,
  T_VariantsData,
} from "dtos/offer-assistant.dto";
import { OfferCard, PayStrip, RecBalTags, XS_TAG } from "./OfferCards";
import {
  GRADE_META,
  GRADE_ORDER,
  LEVEL_TEXT_CLASS,
  UNIT,
  bestBy,
  deriveBadges,
  gradeLevel,
  heldText,
  isRejected,
  joinRefs,
  kMoney,
  mentionShape,
  money,
  plural,
  score,
  shortShape,
  stripScale,
  variantLabel,
  variantNo,
  type T_StripScale,
} from "utils/offer-assistant.utils";
import { useOfferAssistantApi } from "hooks/useOfferAssistant";

const { Text } = Typography;

// ─── Block shell ──────────────────────────────────────────────────────────────

/** Shared block skeleton: header (title + meta) → toolbar → notice → content → actions. */
function Block({
  title,
  meta,
  extra,
  toolbar,
  notice,
  actions,
  flush,
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  /** Right side of the header: view toggles */
  extra?: ReactNode;
  /** Row under the header: segmented controls */
  toolbar?: ReactNode;
  notice?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-solid border-gray-200 bg-white">
      <header className="flex items-start gap-2 border-0 border-b border-solid border-gray-100 py-2.5 pl-3.5 pr-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Text strong className="text-[13.5px] leading-5">
            {title}
          </Text>
          {meta && (
            <Text type="secondary" className="text-xs leading-[17px]">
              {meta}
            </Text>
          )}
        </div>
        {extra && <div className="flex shrink-0 items-center gap-1">{extra}</div>}
      </header>
      {toolbar && <div className="px-3.5 pt-2.5">{toolbar}</div>}
      {notice && (
        <div className="mx-3 mt-2.5 rounded-lg border border-solid border-amber-200 bg-amber-50 px-2.5 py-1.5">
          <Text className="text-[12.5px] !text-amber-800">{notice}</Text>
        </div>
      )}
      <div className={flush ? "" : "p-3"}>{children}</div>
      {actions && (
        <footer className="flex flex-wrap gap-1.5 border-0 border-t border-solid border-gray-100 px-3 py-2.5">
          {actions}
        </footer>
      )}
    </section>
  );
}

// ─── generate_offers ──────────────────────────────────────────────────────────

function OffersBlock({ offers, meta }: { offers: T_AssistantOffer[]; meta: T_AssistantTurnMeta }) {
  const { send } = useOfferAssistantApi();
  const scale = useMemo(() => stripScale(offers), [offers]);
  const badges = useMemo(() => deriveBadges(offers), [offers]);
  const isCustom = meta.limits_state === "custom";

  return (
    <Block
      title={plural(offers.length, "new offer")}
      meta={
        <>
          Within your{" "}
          <Typography.Link className="!text-xs" onClick={() => send("Show my current limits")}>
            {isCustom ? "custom limits" : "current limits"}
          </Typography.Link>
        </>
      }
    >
      <div className="flex flex-col gap-2.5">
        {offers.map((o) => (
          <OfferCard key={o.ref} offer={o} scale={scale} badge={badges[o.ref]} />
        ))}
      </div>
    </Block>
  );
}

// ─── get_offer ────────────────────────────────────────────────────────────────

function SingleOfferBlock({ offer }: { offer: T_AssistantOffer }) {
  const scale = useMemo(() => stripScale([offer]), [offer]);
  const title = offer.kind === "variant" ? `${variantLabel(offer.ref)} of ${offer.parent_ref}` : offer.ref;
  return (
    <Block title={title} meta={offer.held ? heldText(offer.held.anchor, offer.held.value) : offer.summary}>
      <OfferCard offer={offer} scale={scale} density="expanded" />
    </Block>
  );
}

// ─── Over-limit notice (grade_offer + compare_offers) ────────────────────────

const isOverLimit = (meta: T_AssistantTurnMeta, ref: string) => !!meta.breaches_due_amount?.includes(ref);

function OverLimitNotice({ offer, dueAmount }: { offer: T_AssistantOffer; dueAmount: number }) {
  return (
    <div
      role="alert"
      className="whitespace-normal rounded-lg border border-solid border-red-200 bg-red-50 px-2.5 py-1.5"
    >
      <Text className="text-[12px] leading-4 !text-red-800">
        Totals {money(offer.grade.nominal_total)}, more than the {money(dueAmount)} owed
      </Text>
    </div>
  );
}

// ─── grade_offer (1 offer) ────────────────────────────────────────────────────

function WhatIfBlock({ offer, meta }: { offer: T_AssistantOffer; meta: T_AssistantTurnMeta }) {
  const { owed } = useOfferAssistantApi();
  const scale = useMemo(() => stripScale([offer]), [offer]);

  return (
    <Block title="What-if offer" meta={meta.note ?? "Graded against your current limits"} notice={meta.truncated}>
      <div className="flex flex-col gap-2.5">
        {isOverLimit(meta, offer.ref) && <OverLimitNotice offer={offer} dueAmount={meta.due_amount ?? owed} />}
        <OfferCard offer={offer} scale={scale} density="expanded" />
      </div>
    </Block>
  );
}

// ─── show_top_offers ──────────────────────────────────────────────────────────

function TopOffersBlock({ offers, meta }: { offers: T_AssistantOffer[]; meta: T_AssistantTurnMeta }) {
  const { send, busy } = useOfferAssistantApi();
  const key: T_GradeKey = meta.sorted_by ?? "client_utility";
  const scale = useMemo(() => stripScale(offers), [offers]);

  return (
    <Block
      title={`Top ${offers.length} by ${GRADE_META[key].label.toLowerCase()}`}
      meta={meta.note}
      toolbar={
        <Segmented<T_GradeKey>
          size="small"
          value={key}
          disabled={busy}
          options={GRADE_ORDER.map((k) => ({ value: k, label: GRADE_META[k].label }))}
          onChange={(k) => send(`Top ${offers.length} by ${GRADE_META[k].label.toLowerCase()}`)}
        />
      }
    >
      <ol className="m-0 flex list-none flex-col gap-2.5 p-0">
        {offers.map((o, i) => (
          <li key={o.ref} className="-ml-1 flex items-start gap-2">
            <Text
              aria-hidden
              className="w-5 shrink-0 pt-3.5 text-center text-[15px] font-semibold tabular-nums !text-gray-400"
            >
              {i + 1}
            </Text>
            <div className="min-w-0 flex-1">
              <OfferCard offer={o} scale={scale} emphasis={key} />
            </div>
          </li>
        ))}
      </ol>
    </Block>
  );
}

// ─── show_all_offers ──────────────────────────────────────────────────────────

const VISIBLE_ROWS = 8;
const LIST_GRID = "grid grid-cols-[78px_minmax(0,1fr)_62px_52px_52px] items-center gap-x-2 px-3";

/** Dense list: each base offer followed by its variants. */
function AllOffersBlock({ offers }: { offers: T_AssistantOffer[] }) {
  const [showAll, setShowAll] = useState(false);
  const [openRef, setOpenRef] = useState<string | null>(null);
  const scale = useMemo(() => stripScale(offers), [offers]);

  const rows = useMemo(() => {
    const bases = offers.filter((o) => o.kind !== "variant");
    const ordered = bases.flatMap((b) => [b, ...offers.filter((v) => v.parent_ref === b.ref)]);
    const orphans = offers.filter((v) => v.kind === "variant" && !bases.some((b) => b.ref === v.parent_ref));
    return [...ordered, ...orphans];
  }, [offers]);

  const baseCount = offers.filter((o) => o.kind === "base").length;
  const varCount = offers.length - baseCount;
  const visible = showAll ? rows : rows.slice(0, VISIBLE_ROWS);

  return (
    <Block
      title="All offers in this chat"
      meta={`${plural(baseCount, "offer")}${varCount ? ` and ${plural(varCount, "variant")}` : ""}`}
      flush
      actions={
        !showAll && rows.length > VISIBLE_ROWS ? (
          <Button type="link" size="small" onClick={() => setShowAll(true)}>
            Show all {rows.length}
          </Button>
        ) : undefined
      }
    >
      <div role="table" aria-label="All offers">
        <div role="row" className={`${LIST_GRID} items-end pb-1.5 pt-2`}>
          <span role="columnheader" />
          <Text role="columnheader" type="secondary" className="text-[11px] leading-[14px]">
            Shape
          </Text>
          {GRADE_ORDER.map((k) => (
            <Text key={k} role="columnheader" type="secondary" className="text-right text-[11px] leading-[14px]">
              {GRADE_META[k].label}
            </Text>
          ))}
        </div>

        {visible.map((o) => {
          const isOpen = openRef === o.ref;
          const toggle = () => setOpenRef(isOpen ? null : o.ref);
          const isVariant = o.kind === "variant";
          return (
            <div key={o.ref}>
              <div
                role="row"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={toggle}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle();
                  }
                }}
                className={`${LIST_GRID} cursor-pointer py-2 hover:bg-gray-50 ${isOpen ? "bg-gray-50" : ""}`}
              >
                <Text
                  role="cell"
                  className={`whitespace-nowrap text-[12.5px] ${isVariant ? "pl-3 font-medium !text-gray-700" : "font-semibold"}`}
                >
                  {isVariant ? variantLabel(o.ref) : o.ref}
                </Text>
                <span role="cell" className="flex min-w-0 flex-col gap-1">
                  <PayStrip v={o.variables} scale={scale} height={10} tooltips={false} />
                  <Text className="truncate text-[11.5px] !text-gray-600">{shortShape(o.variables)}</Text>
                </span>
                {GRADE_ORDER.map((k) => (
                  <Text
                    key={k}
                    role="cell"
                    className={`text-right text-[12.5px] font-semibold tabular-nums ${LEVEL_TEXT_CLASS[gradeLevel(k, o.grade[k])]}`}
                  >
                    {GRADE_META[k].format(o.grade[k])}
                  </Text>
                ))}
              </div>
              {isOpen && (
                <div className="px-3 pb-3 pt-0.5">
                  <OfferCard offer={o} scale={scale} density="expanded" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Block>
  );
}

// ─── compare_offers ───────────────────────────────────────────────────────────

type T_CompareRow = { label: string; value: (o: T_AssistantOffer) => string; grade?: T_GradeKey };

const COMPARE_ROWS: T_CompareRow[] = [
  { label: "Upfront", value: (o) => (o.variables.upfront_amount ? money(o.variables.upfront_amount) : "None") },
  { label: "Payment", value: (o) => `${money(o.variables.periodic_amount)}/${UNIT[o.variables.payment_frequency]}` },
  { label: "Number of payments", value: (o) => String(o.variables.total_period) },
  {
    label: "Deferral",
    value: (o) => (o.variables.deferral_weeks ? plural(o.variables.deferral_weeks, "week") : "None"),
  },
  { label: "Total paid", value: (o) => money(o.grade.nominal_total) },
  { label: "Value to creditor", value: (o) => money(o.grade.economic_worth), grade: "economic_worth" },
  {
    label: "Affordability",
    value: (o) => GRADE_META.client_utility.format(o.grade.client_utility),
    grade: "client_utility",
  },
  {
    label: "Creditor acceptance",
    value: (o) => GRADE_META.creditor_acceptance.format(o.grade.creditor_acceptance),
    grade: "creditor_acceptance",
  },
];

const CELL =
  "h-9 whitespace-nowrap border-0 border-r border-t border-solid border-gray-100 px-2.5 text-left text-[12.5px] font-normal tabular-nums last:border-r-0";
const LABEL_CELL = "sticky left-0 z-[1] w-[124px] min-w-[124px] bg-white pl-3.5 text-xs !text-gray-500";

/** One plain-language reason, derived from which grades the winner leads on. */
function verdictReason(best: T_AssistantOffer, offers: T_AssistantOffer[]) {
  const wins: string[] = [];
  if (bestBy(offers, "client_utility").ref === best.ref) wins.push("Most affordable");
  if (bestBy(offers, "creditor_acceptance").ref === best.ref)
    wins.push(wins.length ? "the highest creditor acceptance" : "Highest creditor acceptance");
  if (bestBy(offers, "economic_worth").ref === best.ref)
    wins.push(wins.length ? "the highest value to creditor" : "Highest value to creditor");
  if (!wins.length) return "The best balance across all three grades.";
  return wins.length === 1
    ? `${wins[0]} of the ${offers.length}.`
    : `${wins.slice(0, -1).join(", ")} with ${wins[wins.length - 1]}.`;
}

/** compare_offers, and grade_offer with 2+ what-ifs (detected from the offers' UI kind). */
function CompareBlock({ offers, meta }: { offers: T_AssistantOffer[]; meta: T_AssistantTurnMeta }) {
  const { owed } = useOfferAssistantApi();
  const scale = useMemo(() => stripScale(offers), [offers]);
  const isWhatIf = offers[0]?.kind === "whatif";
  const dueAmount = meta.due_amount ?? owed;
  // Winner comes only from the backend — null means nothing qualifies.
  const best = meta.recommended_ref ? offers.find((o) => o.ref === meta.recommended_ref) : undefined;
  const bestByGrade = useMemo(() => {
    const out: Partial<Record<T_GradeKey, string>> = {};
    GRADE_ORDER.forEach((k) => (out[k] = bestBy(offers, k).ref));
    return out;
  }, [offers]);

  if (!offers.length) return null;

  return (
    <Block
      title={isWhatIf ? `Comparing ${offers.length} what-if offers` : `Comparing ${offers.length} offers`}
      meta={meta.note ?? "Best value in each graded row is bold"}
      notice={meta.truncated}
      flush
    >
      {best ? (
        <div className="mx-3 mt-3 flex flex-col gap-0.5 rounded-lg border border-solid border-amber-200 bg-amber-50 px-3 py-2.5">
          <Text strong className="text-[13px] !text-amber-900">
            {isWhatIf ? `★ ${best.ref} is the most suitable` : `★ Best overall: ${best.ref}`}
          </Text>
          {!isWhatIf && (
            <Text className="text-[12.5px] leading-[18px] !text-amber-800">{verdictReason(best, offers)}</Text>
          )}
        </div>
      ) : (
        meta.no_recommendation_reason && (
          <div className="mx-3 mt-3 rounded-lg border border-solid border-gray-200 bg-gray-50 px-3 py-2.5">
            <Text strong className="text-[13px] !text-gray-800">
              None of these fit within the {money(dueAmount)} owed
            </Text>
          </div>
        )
      )}

      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th scope="col" className={`${CELL} ${LABEL_CELL} border-t-0`}>
                <span className="sr-only">Offer</span>
              </th>
              {offers.map((o) => {
                const isWin = o.ref === best?.ref;
                return (
                  <th
                    key={o.ref}
                    scope="col"
                    className={`${CELL} h-auto min-w-[128px] border-t-[3px] py-2 align-top ${
                      isWin ? "border-t-amber-500 bg-amber-50" : "border-t-transparent"
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-1">
                        <Text strong className="text-[13px]">
                          {o.ref}
                        </Text>
                        {isWin && (
                          <Tag color="gold" bordered={false} className={XS_TAG}>
                            ★ Best
                          </Tag>
                        )}
                        {o.balanced && (
                          <Tag color="cyan" bordered={false} className={XS_TAG}>
                            ⚖ Balanced
                          </Tag>
                        )}
                      </span>
                      {isWhatIf ? (
                        <Tag className={`${XS_TAG} self-start !border-dashed !bg-white`}>What-if, not saved</Tag>
                      ) : (
                        <Text type="secondary" className="text-[11px]">
                          {o.kind === "variant" ? `Variant of ${o.parent_ref}` : "\u00A0"}
                        </Text>
                      )}
                      <PayStrip v={o.variables} scale={scale} height={14} />
                      {isOverLimit(meta, o.ref) && <OverLimitNotice offer={o} dueAmount={dueAmount} />}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((r) => (
              <tr key={r.label}>
                <th scope="row" className={`${CELL} ${LABEL_CELL}`}>
                  {r.label}
                </th>
                {offers.map((o) => {
                  const isBest = !!r.grade && offers.length > 1 && bestByGrade[r.grade] === o.ref;
                  const color =
                    r.grade && r.grade !== "economic_worth"
                      ? LEVEL_TEXT_CLASS[gradeLevel(r.grade, o.grade[r.grade])]
                      : "";
                  return (
                    <td key={o.ref} className={`${CELL} ${color} ${isBest ? "bg-violet-50 !font-bold" : ""}`}>
                      {r.value(o)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Block>
  );
}

// ─── Variant trade-off map ────────────────────────────────────────────────────

const MAP_W = 400;
const MAP_H = 240;
const MAP_M = { l: 44, r: 18, t: 16, b: 30 };

function mapAxis(key: T_GradeKey, values: number[]) {
  let min = Math.min(...values);
  let max = Math.max(...values);
  const pad = (max - min) * 0.25 || (key === "economic_worth" ? 5000 : 0.05);
  min -= pad;
  max += pad;
  if (key !== "economic_worth") {
    min = Math.max(0, min);
    max = Math.min(1, max);
  }
  // Every grade is higher-is-better, so no axis is reversed: higher sits further from the origin.
  const pos = (v: number) => (v - min) / (max - min);
  const ticks = [0, 1, 2, 3].map((i) => min + ((max - min) * (i + 0.5)) / 4);
  const fmt = (v: number) => (key === "economic_worth" ? kMoney(v) : score(v));
  return { pos, ticks, fmt };
}

/** Trade-off map of the two free grades. Higher is better on both axes, so top-right is best. */
function VariantMap({
  source,
  variants,
  xKey,
  yKey,
  scale,
  onPick,
}: {
  source: T_AssistantOffer;
  variants: T_AssistantOffer[];
  xKey: T_GradeKey;
  yKey: T_GradeKey;
  scale: T_StripScale;
  onPick: (ref: string) => void;
}) {
  const all = [source, ...variants];
  const ax = mapAxis(
    xKey,
    all.map((o) => o.grade[xKey]),
  );
  const ay = mapAxis(
    yKey,
    all.map((o) => o.grade[yKey]),
  );
  const px = (o: T_AssistantOffer) => MAP_M.l + ax.pos(o.grade[xKey]) * (MAP_W - MAP_M.l - MAP_M.r);
  const py = (o: T_AssistantOffer) => MAP_H - MAP_M.b - ay.pos(o.grade[yKey]) * (MAP_H - MAP_M.t - MAP_M.b);
  const sx = px(source);
  const sy = py(source);

  const tip = (o: T_AssistantOffer) => (
    <div className="flex w-[196px] flex-col gap-2">
      <Text strong className="text-[12.5px]">
        {o === source
          ? `${o.ref} (source)`
          : `${o.recommended ? "★ " : ""}${o.balanced ? "⚖ " : ""}Variant ${variantNo(o.ref)}`}
      </Text>
      <PayStrip v={o.variables} scale={scale} height={10} tooltips={false} />
      <Text className="text-[11.5px] !text-gray-600">{mentionShape(o.variables)}</Text>
      <span className="flex justify-between gap-2">
        {[yKey, xKey].map((k) => (
          <Text key={k} className="text-[11.5px] tabular-nums !text-gray-600">
            {GRADE_META[k].label}{" "}
            <b className={LEVEL_TEXT_CLASS[gradeLevel(k, o.grade[k])]}>{GRADE_META[k].format(o.grade[k])}</b>
          </Text>
        ))}
      </span>
    </div>
  );

  return (
    <figure className="m-0 px-3 pb-3 pt-3.5" aria-label={`${GRADE_META[yKey].label} against ${GRADE_META[xKey].label}`}>
      <Text type="secondary" className="mb-1 ml-2 block text-[11px]">
        {GRADE_META[yKey].label} ↑
      </Text>
      <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} width="100%" role="group" className="block overflow-visible">
        <rect
          x={sx}
          y={MAP_M.t}
          width={MAP_W - MAP_M.r - sx}
          height={sy - MAP_M.t}
          fill="rgba(124, 92, 252, .06)"
          stroke="#C9BDFE"
          strokeDasharray="3 3"
        />
        <text x={MAP_W - MAP_M.r - 6} y={MAP_M.t + 13} textAnchor="end" fontSize={10.5} fill="#6246EA">
          Better on both
        </text>
        {ay.ticks.map((t) => {
          const y = MAP_H - MAP_M.b - ay.pos(t) * (MAP_H - MAP_M.t - MAP_M.b);
          return (
            <g key={`y${t}`}>
              <line x1={MAP_M.l} x2={MAP_W - MAP_M.r} y1={y} y2={y} stroke="#F3F4F6" />
              <text x={MAP_M.l - 6} y={y + 3.5} textAnchor="end" fontSize={10.5} fill="#9CA3AF">
                {ay.fmt(t)}
              </text>
            </g>
          );
        })}
        {ax.ticks.map((t) => (
          <text
            key={`x${t}`}
            x={MAP_M.l + ax.pos(t) * (MAP_W - MAP_M.l - MAP_M.r)}
            y={MAP_H - MAP_M.b + 16}
            textAnchor="middle"
            fontSize={10.5}
            fill="#9CA3AF"
          >
            {ax.fmt(t)}
          </text>
        ))}
        <line x1={MAP_M.l} x2={MAP_M.l} y1={MAP_M.t} y2={MAP_H - MAP_M.b} stroke="#D1D5DB" />
        <line x1={MAP_M.l} x2={MAP_W - MAP_M.r} y1={MAP_H - MAP_M.b} y2={MAP_H - MAP_M.b} stroke="#D1D5DB" />

        {all.map((o) => {
          const x = px(o);
          const y = py(o);
          const isSrc = o === source;
          const labelFill = isSrc ? "#6B7280" : o.recommended ? "#8A5A13" : "#4C3AB8";
          return (
            <Tooltip key={o.ref} title={tip(o)} color="#fff">
              <g
                className={`outline-none ${isSrc ? "cursor-default" : "cursor-pointer"}`}
                tabIndex={isSrc ? -1 : 0}
                role={isSrc ? undefined : "button"}
                aria-label={isSrc ? undefined : `Open ${o.ref}`}
                onClick={() => !isSrc && onPick(o.ref)}
                onKeyDown={(e) => {
                  if (!isSrc && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onPick(o.ref);
                  }
                }}
              >
                <circle cx={x} cy={y} r={12} fill="transparent" />
                {isSrc ? (
                  <rect
                    x={x - 5}
                    y={y - 5}
                    width={10}
                    height={10}
                    transform={`rotate(45 ${x} ${y})`}
                    fill="#fff"
                    stroke="#6B7280"
                    strokeWidth={1.5}
                  />
                ) : o.recommended ? (
                  <text x={x} y={y + 6} textAnchor="middle" fontSize={17} fill="#D69E2E">
                    ★
                  </text>
                ) : (
                  <circle cx={x} cy={y} r={5} fill="#7C5CFC" stroke="#fff" strokeWidth={2} />
                )}
                {!isSrc && o.balanced && <circle cx={x} cy={y} r={9} fill="none" stroke="#0F766E" strokeWidth={2} />}
                <text x={x + 11} y={y + 4} fontSize={11.5} fontWeight={600} fill={labelFill}>
                  {isSrc ? "Source" : `V${variantNo(o.ref)}`}
                </text>
              </g>
            </Tooltip>
          );
        })}
      </svg>
      <figcaption className="mt-1 text-right">
        <Text type="secondary" className="text-[11px]">
          {GRADE_META[xKey].label}, higher is better →
        </Text>
      </figcaption>
    </figure>
  );
}

// ─── generate_variants / show_variants ────────────────────────────────────────

const FREE_LABEL: Record<T_GradeKey, string> = {
  economic_worth: "value",
  client_utility: "affordability",
  creditor_acceptance: "creditor acceptance",
};

type T_VariantRow = T_AssistantOffer & { isSource?: boolean };

/** Every grade sorts highest-first by default. */
const BEST_ORDER: SortOrder = "descend";

const VARIANT_TABLE_CLS = [
  "[&_.ant-table-thead>tr>th]:!pt-2.5",
  "[&_.ant-table-thead>tr>th]:!align-bottom",
  "[&_.ant-table-thead>tr>th]:!text-[11px]",
  "[&_.ant-table-thead>tr>th]:!font-normal",
  "[&_.ant-table-thead>tr>th]:!leading-[14px]",
  "[&_.ant-table-column-sorters]:justify-end",
  "[&_.ant-table-column-title]:!flex-initial",
  "[&_td]:cursor-pointer",
].join(" ");

const ROW_CLS = {
  source: "[&>td]:!cursor-default [&>td]:!bg-gray-50 [&>td]:!text-gray-500",
  rec: "[&>td]:!bg-amber-50/60 [&>td:first-child]:shadow-[inset_3px_0_0_#D69E2E]",
  bal: "[&>td:first-child]:shadow-[inset_3px_0_0_#0F766E]",
};

function Delta({ k, d }: { k: T_GradeKey; d: number }) {
  if (Math.abs(d) < 1e-9) return null;
  const better = d > 0;
  const mag = k === "economic_worth" ? kMoney(Math.abs(d)) : score(Math.abs(d));
  return (
    <Text
      className={`text-[11px] font-medium ${better ? "!text-green-700" : "!text-red-700"}`}
      aria-label={`${better ? "better" : "worse"} by ${mag}`}
    >
      {d > 0 ? "▲" : "▼"}
      {mag}
    </Text>
  );
}

function VariantMatrixBlock({ data, meta }: { data: T_VariantsData; meta: T_AssistantTurnMeta }) {
  const api = useOfferAssistantApi();
  const src = data.source;
  const free = GRADE_ORDER.filter((k) => k !== data.anchor);
  // Keep API order by default; sorting activates only on user interaction.
  const [sort, setSort] = useState<{ key: T_GradeKey; order: SortOrder } | null>(null);
  const [view, setView] = useState<"table" | "map">("table");
  const [expanded, setExpanded] = useState<Key[]>([]);
  const scale = useMemo(() => stripScale([src, ...data.variants]), [src, data.variants]);
  const rows = useMemo<T_VariantRow[]>(() => [{ ...src, isSource: true }, ...data.variants], [src, data.variants]);

  // Value on x when it's free (default anchor → Value × Creditor acceptance); otherwise acceptance on x.
  const xKey: T_GradeKey = free.includes("economic_worth") ? "economic_worth" : "creditor_acceptance";
  const yKey: T_GradeKey = free.find((k) => k !== xKey) ?? "creditor_acceptance";

  const gradeColumn = (k: T_GradeKey): TableColumnsType<T_VariantRow>[number] => {
    const m = GRADE_META[k];
    const isAnchor = k === data.anchor;
    return {
      key: k,
      align: "right",
      width: 84,
      title: isAnchor ? (
        <span className="inline-flex items-end gap-0.5 text-gray-400">
          {m.label} <TbLock />
        </span>
      ) : (
        m.label
      ),
      showSorterTooltip: false,
      // The source row stays pinned on top in either direction.
      sorter: isAnchor
        ? undefined
        : (a, b, order) =>
            a.isSource
              ? order === "descend"
                ? 1
                : -1
              : b.isSource
                ? order === "descend"
                  ? -1
                  : 1
                : a.grade[k] - b.grade[k],
      sortOrder: !isAnchor && sort?.key === k ? sort.order : null,
      sortDirections: isAnchor ? undefined : ["descend", "ascend", "descend"],
      render: (_, r) =>
        isAnchor ? (
          <Text className="tabular-nums !text-gray-400">{m.format(r.grade[k])}</Text>
        ) : (
          <div className="flex flex-col items-end gap-px">
            <Text
              className={`text-[13px] font-semibold tabular-nums ${r.isSource ? "" : LEVEL_TEXT_CLASS[gradeLevel(k, r.grade[k])]}`}
            >
              {m.format(r.grade[k])}
            </Text>
            {!r.isSource && <Delta k={k} d={r.grade[k] - src.grade[k]} />}
          </div>
        ),
    };
  };

  const columns: TableColumnsType<T_VariantRow> = [
    {
      key: "ref",
      width: 132,
      render: (_, r) => {
        if (r.isSource) return <Text className="text-xs font-semibold !text-gray-500">Source</Text>;
        const color = r.recommended ? "!text-amber-800" : r.balanced ? "!text-teal-700" : "";
        const marker = r.recommended ? "★ " : r.balanced ? "⚖ " : "";
        return (
          <div className="flex flex-col items-start gap-1">
            <Text className={`whitespace-nowrap text-[12.5px] font-semibold ${color}`}>
              {marker}V{variantNo(r.ref)}
            </Text>
            {(r.recommended || r.balanced) && (
              <span className="flex flex-wrap gap-1">
                <RecBalTags offer={r} xs />
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "shape",
      title: "Shape",
      render: (_, r) => (
        <div className="flex min-w-0 flex-col gap-1">
          <PayStrip v={r.variables} scale={scale} height={12} muted={r.isSource} tooltips={!r.isSource} />
          <Text className="truncate text-[11.5px] !text-gray-600">
            {r.isSource ? `${r.ref} · ` : ""}
            {shortShape(r.variables)}
          </Text>
        </div>
      ),
    },
    ...GRADE_ORDER.map(gradeColumn),
  ];

  const selectedHere = data.variants.filter((v) => api.isSelected(v.ref)).map((v) => v.ref);
  const pick = (ref: string) => {
    setView("table");
    setExpanded([ref]);
  };

  const recRef = meta.recommended_ref ?? data.variants.find((v) => v.recommended)?.ref;
  const balRef = meta.balanced_ref ?? data.variants.find((v) => v.balanced)?.ref;
  const recVariant = data.variants.find((v) => v.ref === recRef);
  const balVariant = data.variants.find((v) => v.ref === balRef);
  const isSamePick = !!recRef && recRef === balRef;
  const recPhrase = data.anchor === "client_utility" ? "most likely to be accepted" : "the top pick";

  return (
    <Block
      title={`Variants of ${src.ref}`}
      meta={
        <span className="inline-flex items-center gap-1">
          <TbLock /> {heldText(data.anchor, data.anchor_value)}
        </span>
      }
      extra={
        <Segmented
          size="small"
          value={view}
          onChange={(v) => setView(v as "table" | "map")}
          options={[
            { value: "table", label: "Table" },
            { value: "map", label: "Map" },
          ]}
        />
      }
      notice={
        data.variants.length < data.requested
          ? `Showing ${data.variants.length} of ${data.requested} — the target sits near the edge of what's possible.`
          : undefined
      }
      flush
      actions={
        <>
          <Button
            size="small"
            disabled={selectedHere.length < 2}
            onClick={() => api.send(`Compare ${joinRefs(selectedHere)}`)}
          >
            {selectedHere.length >= 2 ? `Compare ${selectedHere.length} selected` : "Compare selected"}
          </Button>
          <Button type="link" size="small" onClick={() => api.insert("Make variants of @")}>
            Make variants of another offer
          </Button>
        </>
      }
    >
      {(recVariant || balVariant) && (
        <div
          aria-live="polite"
          className="mx-3 mt-2 flex flex-wrap gap-x-2.5 gap-y-1 rounded-lg border border-solid border-gray-100 bg-gray-50 px-2.5 py-2"
        >
          {isSamePick && recVariant ? (
            <Text className="text-[12.5px] !text-gray-700">
              <strong>V{variantNo(recVariant.ref)}</strong> is best on every grade
            </Text>
          ) : (
            <>
              {recVariant && (
                <Text className="text-[12.5px] !text-gray-700">
                  <Tag color="gold" bordered={false} className={`${XS_TAG} !mr-1`}>
                    ★
                  </Tag>
                  <strong>V{variantNo(recVariant.ref)}</strong> is {recPhrase}
                </Text>
              )}
              {recVariant && balVariant && (
                <Text aria-hidden type="secondary">
                  ·
                </Text>
              )}
              {balVariant && (
                <Text className="text-[12.5px] !text-gray-700">
                  <Tag color="cyan" bordered={false} className={`${XS_TAG} !mr-1`}>
                    ⚖
                  </Tag>
                  <strong>V{variantNo(balVariant.ref)}</strong> balances {free.map((k) => FREE_LABEL[k]).join(" and ")}
                </Text>
              )}
            </>
          )}
        </div>
      )}

      {view === "table" ? (
        <Table<T_VariantRow>
          className={VARIANT_TABLE_CLS}
          size="small"
          rowKey="ref"
          tableLayout="fixed"
          scroll={{ x: 578 }}
          pagination={false}
          dataSource={rows}
          columns={columns}
          rowClassName={(r) =>
            r.isSource ? ROW_CLS.source : r.recommended ? ROW_CLS.rec : r.balanced ? ROW_CLS.bal : ""
          }
          onChange={(_, __, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            if (s?.columnKey) {
              const key = s.columnKey as T_GradeKey;
              setSort({ key, order: s.order ?? BEST_ORDER });
            }
          }}
          rowSelection={{
            columnWidth: 30,
            hideSelectAll: true,
            selectedRowKeys: selectedHere,
            onSelect: (r) => api.toggleSelect(r.ref),
            renderCell: (_, r, __, node) => (r.isSource ? null : node),
          }}
          expandable={{
            expandedRowKeys: expanded,
            onExpandedRowsChange: (keys) => setExpanded([...keys]),
            expandRowByClick: true,
            showExpandColumn: false,
            rowExpandable: (r) => !r.isSource,
            expandedRowRender: (r) => (
              <div className="cursor-default pt-0.5">
                <OfferCard offer={r} scale={scale} density="expanded" />
              </div>
            ),
          }}
        />
      ) : (
        <VariantMap source={src} variants={data.variants} xKey={xKey} yKey={yKey} scale={scale} onPick={pick} />
      )}
    </Block>
  );
}

// ─── get_context / update_context ─────────────────────────────────────────────

const LIMIT_META: Record<
  T_LimitKey,
  { label: string; format: (n: number) => string; customNote?: (n: number) => string }
> = {
  max_upfront: { label: "Max upfront", format: money },
  max_periodic: { label: "Max per payment", format: (n) => `${money(n)}/week` },
  max_term_weeks: {
    label: "Longest term",
    format: (n) => plural(n, "week"),
    customNote: (n) => `About ${Math.round(n / 4.345)} months`,
  },
  max_deferral_weeks: { label: "Longest pause", format: (n) => plural(n, "week") },
};

function LimitsBlock({ data, justUpdated }: { data: T_AssistantContextData; justUpdated: boolean }) {
  const { insert } = useOfferAssistantApi();
  const n = data.changed.length;

  return (
    <Block
      title="Your limits"
      meta={justUpdated && n ? `Updated ${plural(n, "limit")}` : "Derived from the client's finances"}
      flush
    >
      <ul className="m-0 list-none p-0">
        {data.limits.map((l, i) => {
          const m = LIMIT_META[l.key];
          const isCustom = l.source === "custom";
          const note = isCustom
            ? m.customNote
              ? m.customNote(l.value)
              : `Default would be ${m.format(l.default_value)}`
            : null;
          const isChanged = justUpdated && data.changed.includes(l.key);
          return (
            <li
              key={l.key}
              className={`grid grid-cols-[minmax(0,1fr)_auto_64px_28px] items-center gap-x-3 py-2.5 pl-3.5 pr-2.5 ${
                i > 0 ? "border-0 border-t border-solid border-gray-100" : ""
              } ${isChanged ? "bg-violet-50" : ""}`}
            >
              <Text className="text-[13px] !text-gray-700">{m.label}</Text>
              <span className="flex flex-col items-end">
                <Text strong className="text-[13.5px] tabular-nums">
                  {m.format(l.value)}
                </Text>
                {note && (
                  <Text type="secondary" className="whitespace-nowrap text-[11.5px]">
                    {note}
                  </Text>
                )}
              </span>
              <Tag color={isCustom ? "purple" : undefined} bordered={false} className="!m-0 justify-self-start">
                {isCustom ? "Custom" : "Default"}
              </Tag>
              <Tooltip title={`Change ${m.label.toLowerCase()}`}>
                <Button
                  type="text"
                  size="small"
                  icon={<TbPencil />}
                  className="!text-gray-400 hover:!text-violet-600"
                  aria-label={`Change ${m.label.toLowerCase()}`}
                  onClick={() => insert(`Set ${m.label.toLowerCase()} to `)}
                />
              </Tooltip>
            </li>
          );
        })}
      </ul>
    </Block>
  );
}

// ─── Rejected variant request ─────────────────────────────────────────────────

function RejectedBlock({ data }: { data: T_RejectedVariantsData }) {
  const { send, insert } = useOfferAssistantApi();
  const m = GRADE_META[data.anchor];
  const reach = m.format(data.reachable_value);
  // Value for the message text — integer $ for money; creditor acceptance on the displayed ×10 scale
  // (the backend understands it); affordability stays raw 0–1, unchanged.
  const rawBound =
    data.anchor === "economic_worth"
      ? String(Math.round(data.reachable_value))
      : data.anchor === "creditor_acceptance"
        ? m.format(data.reachable_value)
        : data.reachable_value.toFixed(3);

  const rows: [string, ReactNode][] = [
    [
      "You asked for",
      <strong key="a">
        {m.label} of {m.format(data.requested_value)}
      </strong>,
    ],
    ["Why", data.reason],
    ["Reachable", <strong key="r">The highest possible is {reach}</strong>],
  ];

  return (
    <section
      role="alert"
      className="flex flex-col gap-3 rounded-xl border border-solid border-amber-200 bg-amber-50/60 p-3.5"
    >
      <div className="flex flex-col gap-0.5">
        <Text strong className="text-[13.5px] !text-amber-900">
          {` Can't hold ${m.label.toLowerCase()} there`}
        </Text>
        <Text className="text-xs !text-amber-800">Variants of {data.source_ref}</Text>
      </div>

      <dl className="m-0 grid grid-cols-[92px_minmax(0,1fr)] gap-x-3 gap-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt>
              <Text className="text-[12.5px] !text-amber-800">{label}</Text>
            </dt>
            <dd className="m-0">
              <Text className="text-[12.5px] !text-amber-950 [&_strong]:font-medium">{value}</Text>
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex items-center gap-1.5">
        <Button
          type="primary"
          onClick={() => send(`Make variants of ${data.source_ref} with ${m.label.toLowerCase()} ${rawBound}`)}
        >
          Use {reach}
        </Button>
        <Button
          type="text"
          className="!text-amber-900"
          onClick={() => insert(`Make variants of ${data.source_ref} keeping `)}
        >
          Pick a different anchor
        </Button>
      </div>
    </section>
  );
}

// ─── Dispatcher ───────────────────────────────────────────────────────────────

/** Maps a turn's `action` to its block. clarify / out_of_scope render message-only. */
export function ResultBlock({ turn }: { turn: T_AssistantTurn }) {
  switch (turn.action) {
    case "generate_offers":
      return <OffersBlock offers={turn.data} meta={turn.meta} />;
    case "show_all_offers":
      return <AllOffersBlock offers={turn.data} />;
    case "show_top_offers":
      return <TopOffersBlock offers={turn.data} meta={turn.meta} />;
    case "get_offer":
      return turn.data[0] ? <SingleOfferBlock offer={turn.data[0]} /> : null;
    case "grade_offer":
      if (turn.data.length > 1) return <CompareBlock offers={turn.data} meta={turn.meta} />;
      return turn.data[0] ? <WhatIfBlock offer={turn.data[0]} meta={turn.meta} /> : null;
    case "compare_offers":
      return <CompareBlock offers={turn.data} meta={turn.meta} />;
    case "generate_variants":
    case "show_variants":
      return isRejected(turn.data) ? (
        <RejectedBlock data={turn.data} />
      ) : (
        <VariantMatrixBlock data={turn.data} meta={turn.meta} />
      );
    case "get_context":
    case "update_context":
      return <LimitsBlock data={turn.data} justUpdated={turn.action === "update_context"} />;
    default:
      return null;
  }
}
